import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { logout } from "../../store/slices/authSlice";
import { getNavigation } from "../../utils/navigation";
import useGlobalChatUnread from "../inbox/useGlobalChatUnread";
import { usePermission } from "../usePermission";
import { ROLES, isLawyerOnlyRoles } from "../../config/roles";
import { buildMegaCategories } from "../../constants/megaMenuCategories";
import { scoutRequestService } from "../../services/scoutRequestService";
import { whatsNewService } from "../../services/whatsNewService";
import { fetchLoungeUnread } from "../../store/slices/loungeSlice";

// Launch-only "NEW" pill on the agent Scout Requests quick-access chip (design
// 3a: "New pill only for launch"). Remove after launch.
const SHOW_SCOUT_REQUESTS_NEW_PILL = true;

export const useHeaderNavigation = (profileData, kycVerified) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentRole, roles } = useSelector((state) => state.auth);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isRoleSwitching, setIsRoleSwitching] = useState(false);
  const [agencySwitchOverride, setAgencySwitchOverride] = useState(null);

  // Permission checks
  const canEditRoles = usePermission("edit_roles");
  // Gate for the Banned Agents menu entry. PART A of the pitch-lifecycle work
  // order: "every club member who has access to Club Tenders — the same
  // permission gates this menu". `view_tenders` is that permission (it gates
  // /club/tenders), read from the code, not invented.
  const canViewTenders = usePermission("view_tenders");
  const canCreateTenders = usePermission("create_tenders");

  // Scout-Requests mega-menu badge (design 4a): count of UNSEEN recommendations
  // across the club's open requests. Club roles only; the badge shows only when
  // > 0. Endpoint absence / errors degrade silently to 0 (no badge).
  const isClubRole =
    currentRole === ROLES.ClubAdmin ||
    currentRole === ROLES.Staff ||
    currentRole === ROLES.Coach;
  const [scoutBadge, setScoutBadge] = useState(0);
  useEffect(() => {
    let alive = true;
    if (!kycVerified || !isClubRole) {
      setScoutBadge(0);
      return undefined;
    }
    scoutRequestService.getBadge().then((res) => {
      if (alive && res.success) setScoutBadge(res.data?.new_recommendations || 0);
    });
    return () => {
      alive = false;
    };
  }, [kycVerified, isClubRole, location.pathname]);

  // What's New unread dot (D-003: a plain DOT, not a count). Re-checked on every
  // navigation; the feed page also fires "whatsnew:visited" on open so the dot
  // clears immediately once the visit marker is stamped. Errors degrade to no
  // dot (getUnread already swallows them to unread=false).
  const [whatsNewUnread, setWhatsNewUnread] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!kycVerified) {
      setWhatsNewUnread(false);
      return undefined;
    }
    const check = () => {
      whatsNewService.getUnread().then((res) => {
        if (alive && res.success) setWhatsNewUnread(Boolean(res.unread));
      });
    };
    check();
    const onVisited = () => {
      if (alive) setWhatsNewUnread(false);
    };
    window.addEventListener("whatsnew:visited", onVisited);
    return () => {
      alive = false;
      window.removeEventListener("whatsnew:visited", onVisited);
    };
  }, [kycVerified, location.pathname]);

  // Agent Lounge unread badge (GATE0 §2 / Brief §3): the Sofa nav entry mirrors
  // the in-lounge Activity button's count. Redux holds ONLY this number. Fetched
  // for the agent perspective (Agent + AgencyAdmin, the lounge audience) once
  // KYC-verified, and re-checked on navigation so the badge stays fresh without
  // sitting on the lounge page. Non-agents never fetch (no 403 noise).
  const loungeUnread = useSelector((state) => state.lounge?.unreadCount || 0);
  const isAgentPerspective =
    currentRole === ROLES.Agent || currentRole === ROLES.AgencyAdmin;
  useEffect(() => {
    if (!kycVerified || !isAgentPerspective) return;
    dispatch(fetchLoungeUnread());
  }, [kycVerified, isAgentPerspective, location.pathname, dispatch]);

  const baseNavigationItems = getNavigation(
    currentRole,
    profileData,
    kycVerified,
    user?.permissions || []
  );

  // D26: global unread pill on the "Messages"/Inbox nav item — total unread
  // across all lanes (Stream source, like the tab counts). Decorate the /inbox
  // item with the count; the nav render shows a pill only when > 0.
  const chatUnread = useGlobalChatUnread();
  const navigationItems = useMemo(
    () =>
      baseNavigationItems.map((item) =>
        item.path === "/inbox" ? { ...item, unread: chatUnread } : item,
      ),
    [baseNavigationItems, chatUnread],
  );

  const allSubmenuItems = [
    {
      iconKey: "DISCOVERY",
      title: "Discovery",
      description: "Discover free players ready to join your team.",
      path: "/discovery",
    },
    {
      iconKey: "COMPARE_PLAYERS",
      title: "Compare players",
      description: "Compare player stats effortlessly",
      path: "/compare",
    },
    // Team of the Week (TP_TOTW_BRIEF): Tools entry tile — visibility mirrors
    // Compare/Discovery (all roles except lawyer-only; non-KYC allowed).
    {
      iconKey: "TOTW",
      title: "Team of the Week",
      description:
        "Select between different competitions and rounds to see the best XI of the week on the pitch.",
      path: "/team-of-the-week",
      isNew: true,
    },
    {
      iconKey: "LIVE_MARKET",
      title: "Live Market",
      description: "Explore all available for sale and loan.",
      path: "/market",
    },
    {
      iconKey: "WHATS_NEW",
      title: "What's New",
      // 1b artboard-exact copy (no trailing period) — see TP-FEED handoff.
      description: "Product updates from the team",
      path: "/whats-new",
      dot: whatsNewUnread,
    },
    {
      iconKey: "TRANSFER_NEWS",
      title: "Transfer News",
      description: "Latest transfer updates and rumors.",
      path: "/news",
    },
    // Competitions (TP-CompetitionPage build): CONTENT-category browse page
    // over the TheSports mirror. Visible to every role incl. non-KYC (mirrors
    // Transfer News / TOTW visibility).
    {
      iconKey: "COMPETITIONS",
      title: "Competitions",
      description: "Tables, schedules, top scorers",
      path: "/competitions",
    },
    {
      iconKey: "EVENTS",
      title: "Events",
      description: "Discover and join upcoming events.",
      path: "/events",
    },
    {
      iconKey: "ORGANIZERS",
      title: "Organizers",
      description: "View all verified organisers.",
      path: "/organizers",
    },
    {
      iconKey: "LAWYERS",
      title: "Lawyers",
      description: "View all verified lawyers.",
      path: "/lawyers",
    }
  ];

  const submenuItems = (() => {
    // Define allowed routes for non-KYC verified users
    const allowedNonKycRoutes = [
      "/discovery",
      "/compare",
      "/team-of-the-week",
      "/market",
      "/whats-new",
      "/news",
      // Competitions: provider content, viewable without KYC. Kept in sync
      // with PrivateRoute's NON_KYC_ROUTES (two lists on purpose — D28 lesson).
      "/competitions",
      "/events",
      "/organizers",
      "/lawyers",
    ];

    // Filter based on KYC status and role
    let filteredItems = allSubmenuItems;

    if (!kycVerified) {
      filteredItems = filteredItems.filter((item) =>
        allowedNonKycRoutes.includes(item.path)
      );
    } else if (currentRole === ROLES.Player) {
      filteredItems = filteredItems.filter((item) =>
        ["/discovery", "/compare", "/team-of-the-week", "/whats-new", "/news", "/competitions"].includes(item.path)
      );
    } else if (isLawyerOnlyRoles(roles)) {
      // Lawyer-only: keep the directory/news items, drop the player-scouting
      // tools (Discovery / Compare) that only make sense for agents/clubs. The
      // Lawyers directory ("View all verified lawyers") stays. Dual-role users
      // are not lawyer-only → they keep the full submenu.
      filteredItems = filteredItems.filter((item) =>
        ["/whats-new", "/news", "/competitions", "/events", "/organizers", "/lawyers"].includes(item.path)
      );
    }

    // Enforce role/permission gate for Live Market
    const canAccessLiveMarket =
      currentRole === ROLES.ClubAdmin ||
      (currentRole === ROLES.Staff && canCreateTenders);

    filteredItems = filteredItems.filter(
      (item) => item.path !== "/market" || canAccessLiveMarket
    );

    // In the agent perspective (Agent + AgencyAdmin) "Agent Requests" lives in
    // the ☰ menu — not the top nav bar. "Taskboard" joins it in the ☰ ONLY for
    // the Agent perspective; in the Agency perspective Taskboard stays
    // top-level in the nav bar (Marin, Runde 6 — see navigation.js). Both
    // routes are KYC-gated, so only add them when the account is verified.
    if (
      kycVerified &&
      (currentRole === ROLES.Agent || currentRole === ROLES.AgencyAdmin)
    ) {
      const agentWorkspace = [
        {
          iconKey: "AGENT_REQUESTS",
          title: "Agent Requests",
          description: "Agent-to-agent requests marketplace.",
          path: "/agent-tenders",
        },
        // Scout Requests (design 3a): directly under Agent Requests in Marketplace
        // — both demand streams (agent-to-agent + club-to-agent) in one place.
        {
          iconKey: "SCOUT_REQUESTS",
          title: "Scout Requests",
          description: "Clubs post scouting needs. Put players on their radar.",
          path: "/scout-requests",
        },
        // TP-Last-Minute (design 7a): in Marketplace, right after the demand
        // streams. NEW launch badge; club side mirrors it below.
        {
          iconKey: "LAST_MINUTE",
          title: "Last Minute",
          description: "Players & staff available right now, listed by agents.",
          path: "/last-minute",
          isNew: true,
        },
        // Club-Network N3 (agent side): incoming club search requests.
        {
          iconKey: "SEARCH_REQUESTS",
          title: "Search Requests",
          description: "Search requests clubs sent you.",
          path: "/network/search-requests/inbox",
        },
        // Agent Lounge (design 1q / GATE0 §4): the agents-only feed, filed under
        // the Network mega-menu category. Badge = lounge unread_count (replies +
        // @mentions), shown only when > 0.
        {
          iconKey: "LOUNGE",
          title: "Agent Lounge",
          description: "Agents-only talk: regulations, commissions, window strategy.",
          path: "/lounge",
          badge: loungeUnread > 0 ? loungeUnread : undefined,
        },
      ];
      if (currentRole === ROLES.Agent) {
        agentWorkspace.push({
          iconKey: "TASKBOARD",
          title: "Taskboard",
          description: "Track your agency's deals and tasks.",
          path: "/agency/taskboard",
        });
      }
      // TP Agent Radar (Tools): personal shortlists, both agent roles.
      // Mirrors the club Radar nav entry; categorised into Tools via megaMenuCategories.
      agentWorkspace.push({
        iconKey: "RADAR",
        title: "Radar",
        description: "Your personal shortlists. Save players from their profile.",
        path: "/agent/radar",
      });
      filteredItems = [...agentWorkspace, ...filteredItems];
    }

    // Club-Network (club side): N2 anonymous Agent Requests feed + N3 Trusted
    // Agent search requests. Additive ☰ entries for the club roles, mirroring the
    // agent-side block above. KYC-gated like the rest of the workspace.
    if (
      kycVerified &&
      (currentRole === ROLES.ClubAdmin ||
        currentRole === ROLES.Staff ||
        currentRole === ROLES.Coach)
    ) {
      const clubNetworkWorkspace = [
        {
          iconKey: "AGENT_REQUESTS",
          title: "Agent Requests",
          description: "Browse anonymous transfer requests posted by agents.",
          path: "/network/agent-requests",
        },
        {
          iconKey: "TRUSTED_AGENT",
          title: "Trusted Agent",
          description: "Ask agents you trust to search for a player.",
          path: "/network/trusted-agent",
        },
        // TP-Last-Minute (design 7a, club side): near the transfer-market group.
        {
          iconKey: "LAST_MINUTE",
          title: "Last Minute",
          description: "Players & staff available right now, listed by agents.",
          path: "/last-minute",
          isNew: true,
        },
        // TP-Friendlies (D-006): the market area near Requests, club-only.
        {
          iconKey: "FRIENDLIES",
          title: "Friendlies",
          description: "Find friendly-match opponents, club to club.",
          path: "/friendlies",
          isNew: true,
        },
      ];
      // Scouting group (design 4a): Scout Requests (badge = unseen recs) + Radar
      // (no unread — nothing lands there without a club action). Prepended so the
      // SCOUTING category (first in CATEGORY_DEFS) leads the mega-menu.
      const scoutingWorkspace = [
        {
          iconKey: "SCOUT_REQUESTS",
          title: "Scout Requests",
          description: "Post what you're scouting for. Agents recommend players.",
          path: "/scouting/requests",
          badge: scoutBadge > 0 ? scoutBadge : undefined,
        },
        {
          iconKey: "RADAR",
          title: "Radar",
          description: "Your watchlist. Every entry keeps its recommender.",
          path: "/scouting/radar",
        },
      ];
      filteredItems = [...scoutingWorkspace, ...clubNetworkWorkspace, ...filteredItems];
    }

    return filteredItems;
  })();

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    dispatch(logout());
    navigate("/login");
    setShowLogoutConfirm(false);
  };

  const isActive = (path) => {
    // For home path
    if (path === "/") {
      return location.pathname === "/";
    }
    // For other paths, exact match instead of startsWith
    return location.pathname === path;
  };

  const handleRoleSwitch = (role) => {
    if (isRoleSwitching) return;
    console.log("Header - Switching to role:", role);
    setIsRoleSwitching(true);
    setAgencySwitchOverride(role === ROLES.AgencyAdmin);
    setTimeout(() => {
      dispatch({ type: "auth/switchRole", payload: role });
      setIsRoleSwitching(false);
      setAgencySwitchOverride(null);
    }, 1000);
  };

  const isAgencyOnUI =
    agencySwitchOverride !== null
      ? agencySwitchOverride
      : currentRole === ROLES.AgencyAdmin;

  // Mega-menu (DoD 22): the ☰ panel's "explore" destinations, grouped into
  // categories. Data-driven from the same submenuItems the old ☰ used, so the
  // per-role / per-perspective rules (incl. E-20c) carry over unchanged.
  //
  // TOTW column (Marin 2026-08-14): the CLUB perspective files Team of the
  // Week under SCOUTING (next to Scout Requests + Radar); agents and staff
  // WITHOUT a club keep it under TOOLS. Club signal for Staff/Coach =
  // profileData.club_details — the same field useHeaderProfile reads to show
  // club contact info ("staff/coach with club"); a job-seeking staff has no
  // club_details. buildMegaCategories additionally requires real /scouting/*
  // items before moving the tile (claim-once, never duplicated).
  const staffHasClub = Boolean(
    profileData?.club_details?.name || profileData?.club_details?.id,
  );
  const totwColumn =
    currentRole === ROLES.ClubAdmin ||
    ((currentRole === ROLES.Staff || currentRole === ROLES.Coach) && staffHasClub)
      ? "scouting"
      : "tools";
  const megaCategories = buildMegaCategories(submenuItems, { totwColumn });

  // Quick-access pills: the role's most-used top actions. Data-driven and
  // KYC-gated; empty for roles without a defined shortcut set.
  const quickAccessPills = (() => {
    if (!kycVerified) return [];
    // Scout Requests chip for the agent perspective (design 3a), with a launch-only
    // "NEW" pill; a check-every-visit surface.
    const agentScoutChip = {
      id: "scout-requests",
      label: "Scout Requests",
      path: "/scout-requests",
      iconKey: "SCOUT_REQUESTS",
      newPill: SHOW_SCOUT_REQUESTS_NEW_PILL,
    };
    // TP-Last-Minute quick-access pill (design 7a), launch-only NEW badge.
    const lastMinuteChip = { id: "last-minute", label: "Last Minute", path: "/last-minute", iconKey: "LAST_MINUTE", newPill: true };
    // TP Agent Radar pill, mirroring the club Radar pill exactly.
    const radarChip = [{ id: "radar", label: "Radar", path: "/agent/radar", iconKey: "RADAR" }];
    if (currentRole === ROLES.Agent) {
      return [
        { id: "post-request", label: "Post Request", path: "/agent-tenders/create", iconKey: "POST_REQUEST" },
        { id: "my-requests", label: "My Requests", path: "/agent-tenders/mine", iconKey: "MY_REQUESTS" },
        agentScoutChip,
        lastMinuteChip,
        ...radarChip,
      ];
    }
    if (currentRole === ROLES.AgencyAdmin) {
      return [
        { id: "taskboard", label: "Taskboard", path: "/agency/taskboard", iconKey: "TASKBOARD" },
        agentScoutChip,
        lastMinuteChip,
        ...radarChip,
      ];
    }
    // Club perspective (design 4a): Scout Requests (badge) + Radar (no unread).
    if (isClubRole) {
      return [
        {
          id: "scout-requests",
          label: "Scout Requests",
          path: "/scouting/requests",
          iconKey: "SCOUT_REQUESTS",
          badge: scoutBadge > 0 ? scoutBadge : undefined,
        },
        { id: "radar", label: "Radar", path: "/scouting/radar", iconKey: "RADAR" },
        lastMinuteChip,
      ];
    }
    return [];
  })();

  return {
    navigationItems,
    submenuItems,
    megaCategories,
    quickAccessPills,
    whatsNewUnread,
    showLogoutConfirm,
    setShowLogoutConfirm,
    handleLogout,
    handleConfirmLogout,
    isActive,
    handleRoleSwitch,
    isRoleSwitching,
    isAgencyOnUI,
    canEditRoles,
    canViewTenders,
    canCreateTenders,
  };
};
